/**
 * Authed fetch wrapper for the same-origin /api/*. Attaches the PKCE-issued
 * access token as `Authorization: Bearer <token>`; a 401 clears the token
 * and bounces the user back through the OIDC authorize flow.
 */

import { API_BASE } from "../config.ts";
import { ApiError } from "./api-error.ts";
import { beginLogin, clearToken, getToken } from "./auth.ts";
import { locationsQuery } from "./campaign-card.ts";
import type { CampaignCreate, CampaignSwitch } from "./campaigns.ts";

export class UnauthorizedError extends Error {
	constructor() {
		super("Unauthorized");
		this.name = "UnauthorizedError";
	}
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
	const token = getToken();
	const headers: Record<string, string> = {
		Accept: "application/json",
		...(init?.headers as Record<string, string> | undefined),
	};
	if (token) headers.Authorization = `Bearer ${token}`;
	const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
	if (res.status === 401) {
		clearToken();
		void beginLogin(window.location.pathname + window.location.search);
		throw new UnauthorizedError();
	}
	return res;
}

async function body<T>(res: Response, what: string): Promise<T> {
	/*
	 * A response that is not JSON is a fault, never an empty result.
	 *
	 * This used to swallow it: `.json().catch(() => ({}))` turned the SPA's own
	 * HTML — which Static Assets returns for any unrouted /api path — into `{}`,
	 * and a screen reading `data.accounts` off it crashed three frames later with
	 * "not iterable". The cause was invisible at the point of failure.
	 */
	// Every failure carries the status (lib/api-error.ts): the screens decide what to say
	// by it, and a 403 is the only answer that means «not yours to read».
	const type = res.headers.get("content-type") ?? "";
	if (!type.includes("json")) {
		throw new ApiError(
			`${what}: expected JSON, got ${type || "no content-type"} (${res.status})`,
			res.status,
		);
	}
	const parsed = await res.json().catch(() => null);
	if (parsed === null) throw new ApiError(`${what}: malformed JSON (${res.status})`, res.status);
	if (!res.ok)
		throw new ApiError(
			(parsed as { error?: string }).error ?? `${what} (${res.status})`,
			res.status,
		);
	return parsed as T;
}

const getJson = async <T>(path: string) => body<T>(await authFetch(path), `GET ${path} failed`);

const send = async <T>(method: string, path: string, payload?: unknown) =>
	body<T>(
		await authFetch(path, {
			method,
			headers: { "Content-Type": "application/json" },
			body: payload === undefined ? undefined : JSON.stringify(payload),
		}),
		`${method} ${path} failed`,
	);

/**
 * A write whose failure means something in its body — `created: true` on a 502 is
 * a campaign that exists. So the status and the parsed JSON come back rather than a
 * thrown message, and `lib/campaigns.ts` decides from their structure. An answer
 * that is not JSON is still a fault, thrown, as in `body`.
 */
/**
 * One row of `GET /api/v1/approvals` — a proposed v1 write. `status` is what the worker says a
 * reader should see now; `result` is the route's own answer after Approve, untouched.
 */
export interface Approval {
	id: string;
	account: string;
	route: string;
	targetId: string | null;
	body: Record<string, unknown>;
	observed: Record<string, unknown>;
	origin: "advisor" | "person";
	agentId: string | null;
	proposedBy: string;
	reason: string;
	createdAt: string;
	expiresAt: string;
	status:
		| "pending"
		| "approving"
		| "done"
		| "refused"
		| "unknown"
		| "rejected"
		| "expired"
		| "stale"
		| "over_limit";
	decidedBy: string | null;
	decidedAt: string | null;
	result: { httpStatus: number; body: unknown } | null;
}

export interface Answer {
	status: number;
	body: unknown;
}

const answer = async (method: string, path: string, payload?: unknown): Promise<Answer> => {
	// A payload-less read goes without a body and without the header that announces
	// one: `campaign` below is a GET, and its 404 is information rather than a fault.
	const res = await authFetch(path, {
		method,
		...(payload === undefined
			? {}
			: { headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
	});
	const what = `${method} ${path} failed`;
	const type = res.headers.get("content-type") ?? "";
	if (!type.includes("json")) {
		throw new ApiError(
			`${what}: expected JSON, got ${type || "no content-type"} (${res.status})`,
			res.status,
		);
	}
	const parsed: unknown = await res.json().catch(() => null);
	if (parsed === null) throw new ApiError(`${what}: malformed JSON (${res.status})`, res.status);
	return { status: res.status, body: parsed };
};

/** `answer`, for a multipart body: the runtime sets the content-type with its boundary. */
const answerForm = async (path: string, form: FormData): Promise<Answer> => {
	const res = await authFetch(path, { method: "POST", body: form });
	const what = `POST ${path} failed`;
	const type = res.headers.get("content-type") ?? "";
	if (!type.includes("json")) {
		throw new ApiError(
			`${what}: expected JSON, got ${type || "no content-type"} (${res.status})`,
			res.status,
		);
	}
	const parsed: unknown = await res.json().catch(() => null);
	if (parsed === null) throw new ApiError(`${what}: malformed JSON (${res.status})`, res.status);
	return { status: res.status, body: parsed };
};

// ── Shapes ───────────────────────────────────────────────────────────

export interface Me {
	id: string;
	email?: string;
	role?: string;
	appAccess?: string[];
}

// The ladder lives in ./roles.ts so a test runner can load it — api.ts imports
// config.ts, which reads import.meta.env, and node --test has no such thing.
// Imported for the shapes below and re-exported so callers keep one import site.
import type { Role } from "./roles.ts";
export type { Role };
export { roleAtLeast } from "./roles.ts";
export type EntityType = "agents" | "x_accounts" | "proxies";
export type Permission = "read" | "write";

export interface Grant {
	entityType: EntityType;
	entityId: string;
	permission: Permission;
}

export interface MyAccess {
	userId: string;
	email: string | null;
	role: Role;
	/** True when the role comes from the platform, not a seated member row. */
	inherited: boolean;
	status: string;
	grants: Grant[];
	canAssign: Role[];
}

export interface Member {
	id: string;
	userId: string;
	email: string | null;
	displayName: string | null;
	role: Role;
	status: "active" | "suspended";
	updatedAt: string;
	grants: Grant[];
}

export interface Agent {
	id: string;
	name: string;
	kind: string;
	description: string | null;
	status: string;
	/** console-kit 0.5.0: the machine client's `sub`, or null; null for anyone but the platform. */
	subject?: string | null;
}

export type ActionLevel = "self" | "propose" | "never";

/** `GET /api/v1/agents/:id/controls` — one agent, what is set on it, and the platform's cap. */
export interface AgentControlsAnswer {
	agent: { id: string; name: string; kind: string; status: string };
	policy?: {
		capDayMicro: number | null;
		capTotalMicro: number | null;
		levels: Record<string, ActionLevel>;
		updatedBy: string;
		updatedAt: string;
	} | null;
	scope?: { xAccountId: string; permission: "read" | "write"; campaignIds: string[] | null }[];
	platformCap: { daily: number | null; total: number | null; misconfigured: string[] };
	actions: { key: string; label: string; default: ActionLevel; locked: boolean }[];
	unavailable?: string;
}

/** One row of `GET /api/v1/mcp-calls` — an MCP tool call, as the worker logged it. */
export interface McpCallRow {
	id: string;
	at: string;
	subject: string | null;
	agentId: string | null;
	tool: string;
	status: number;
	ms: number;
	requestId: string | null;
}

/** `GET /api/v1/mcp-calls` — newest first; `unavailable` while 0014 is not applied. */
export interface McpCallsAnswer {
	calls: McpCallRow[];
	unavailable?: string;
}

export interface ProxyRow {
	id: string;
	label: string;
	protocol: string;
	host: string;
	port: number;
	country: string | null;
	credRef: string | null;
	status: string;
}

export interface XAccount {
	id: string;
	/** X's ad account id, e.g. `18ce54d4x5t` — a string, never a number. */
	xAccountId: string;
	name: string;
	/**
	 * Always null in v1 (the contract): X keeps no currency on the account object, only on
	 * its funding instruments, and the worker does not pick one of several.
	 */
	currency: string | null;
	/** As X reports it, e.g. `ACCEPTED`. Null until the account is synced. */
	approvalStatus: string | null;
	/** IANA zone, e.g. `America/Los_Angeles`. */
	timezone: string | null;
	businessName: string | null;
	/*
	 * No `spendCap` and no `amountSpent`. The contract sends `spendCap: null`
	 * until a source is decided, and neither figure is on X's account object —
	 * see the note in `format.ts`. Both come back together, with a migration and
	 * a contract entry, rather than one at a time.
	 */
	status: string;
	syncedAt: string | null;
}

/**
 * One row of `GET /api/v1/signals`: X's word about one object, as the contract's
 * «Signals» declares it. `value` is X's word byte for byte; nothing here reads it.
 */
export interface Signal {
	entity: string;
	entityId: string;
	kind: string;
	value: string;
	firstSeen: string;
	lastSeen: string;
	clearedAt: string | null;
}

export interface SignalsAnswer {
	signals: Signal[];
	truncated: boolean;
	/** The last cycle's result as it recorded it; `okAt` dates the last read. */
	cycle: { okAt?: string | null; readAt?: string } | null;
	/** No cycle has read anything within two intervals. */
	stale: boolean;
	/** Set while migration 0009 is not applied: then `signals` is empty for that reason. */
	unavailable?: string;
}

/**
 * Five figures as the mirror sums them (`GET /api/v1/overview-stats`, contract «Overview
 * stats»). 🔴 `null` means nothing was read for it — never a zero: a campaign X answered
 * with nulls adds 0, a campaign the cycle did not reach is absent.
 */
export interface OverviewFigures {
	billedMicro: number | null;
	impressions: number | null;
	engagements: number | null;
	urlClicks: number | null;
	clicks: number | null;
}

/** One local day of the account: a sum over the campaigns with a row for it. */
export interface OverviewDay extends OverviewFigures {
	day: string;
	campaigns: number;
	partial: boolean;
	/**
	 * Campaigns whose figure for this full day is still X's hourly sum, not its DAY figure —
	 * counted in the day's sum and named; always 0 on `today`.
	 */
	partialCampaigns: number;
	window: { start: string; end: string } | null;
}

/** One campaign over the full days answered (not today). */
export interface OverviewCampaign extends OverviewFigures {
	id: string;
	days: number;
}

/** The last stats cycle's result, as the worker recorded it — the fields this SPA reads. */
export interface OverviewCycle {
	readAt?: string;
	okAt?: string | null;
	notRead?: string[];
	skipped?: string;
	error?: string;
}

/** `GET /api/v1/overview-stats` — from the mirror only; X is not asked. */
export interface OverviewStatsAnswer {
	placement: string;
	metricGroups: string[];
	entity: "campaign";
	days: OverviewDay[];
	today: OverviewDay | null;
	total: OverviewFigures;
	totalWithToday: OverviewFigures;
	campaigns: OverviewCampaign[];
	campaignsListed: number | null;
	readAt: string | null;
	stale: boolean;
	cycle: OverviewCycle | null;
	/** Set while migration 0010 is not applied: then `days` is empty for that reason. */
	unavailable?: string;
}

/**
 * One row of `GET /api/v1/campaigns`, as docs/API-CONTRACT.md declares it. Budgets
 * are X's micro units of `currency`, unconverted.
 */
export interface Campaign {
	id: string;
	name: string;
	/** The switch: `ACTIVE`, `PAUSED` or `DRAFT`. */
	entityStatus: string;
	/** Whether X will deliver — a different question from the switch. */
	servable: boolean;
	reasonsNotServable: string[];
	fundingInstrumentId: string;
	currency: string | null;
	dailyBudgetAmountLocalMicro: number | null;
	totalBudgetAmountLocalMicro: number | null;
	/**
	 * X's `budget_optimization`, read only — `null` when X sends none. A line item's daily
	 * budget is written only under LINE_ITEM (`dailyEditable`, campaign-manage.ts).
	 */
	budgetOptimization?: string | null;
	createdAt: string;
	updatedAt: string;
}

/**
 * One page of `GET /api/v1/campaigns`.
 *
 * `page.nextCursor` is X's own string — `null` is the last page — and
 * `page.pageSize` is the page size X used or was asked for, never a row count and
 * never a total: X's `with_total_count` is exclusive with `cursor`, so nothing
 * upstream has one. `pageSize` is this console's name for what X calls `count`,
 * and it matches ttads, which answers the same field.
 * `truncated` is the same fact as `page.nextCursor !== null`, and is required here
 * for the reason the comment above `agents` gives.
 */
export interface CampaignPage {
	campaigns: Campaign[];
	page: { pageSize: number; nextCursor: string | null };
	truncated: boolean;
}

/**
 * One row of `GET /api/v1/line-items`, as the worker shapes it.
 *
 * 🔴 No `servable` and no `reasonsNotServable`: X's line item object carries
 * neither, so there is no per-line-item delivery signal to show and this console
 * does not invent one as `null` — that would read as X saying it will not serve.
 * The delivery signal on the screen is the campaign's.
 */
export interface LineItem {
	id: string;
	campaignId: string;
	/** X's name for the line item, `null` when none was given (the worker, since twads#171). */
	name: string | null;
	entityStatus: string;
	objective: string;
	productType: string;
	placements: string[];
	bidStrategy: string;
	/** X's number in micro units, and null under `AUTO`, where X sets the price. */
	bidAmountLocalMicro: number | null;
	/**
	 * The line item's own budgets in micro units (the worker, since 2026-10-22). 🔴 Absent
	 * when X's answer does not carry the field, `null` when X reports null — two different
	 * answers, kept apart on the wire. The total is written by the wizard and the Edit form;
	 * the daily one is read only — the worker refuses it until a campaign's
	 * budgetOptimization has been seen.
	 */
	totalBudgetAmountLocalMicro?: number | null;
	dailyBudgetAmountLocalMicro?: number | null;
	/** X's `goal` and `pay_by`, read only (2026-10-23): absent when X's answer does not carry them. */
	goal?: string | null;
	payBy?: string | null;
	startTime: string | null;
	endTime: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface LineItemPage {
	lineItems: LineItem[];
	page: { pageSize: number; nextCursor: string | null };
	truncated: boolean;
}

/**
 * One row of `GET /api/v1/tweets` — a candidate creative.
 *
 * 🔴 `id` is X's `id_str` and never X's numeric `id`: a tweet id exceeds JavaScript's
 * safe integer range, so the number arrives already rounded (the worker drops rows
 * that carry no `id_str` rather than falling back). Nothing in the SPA parses or
 * shortens it.
 */
export interface Tweet {
	id: string;
	text: string | null;
	name: string | null;
	nullcast: boolean | null;
	createdAt: string | null;
	/** X's `conversation_settings` on the read, or null when the row does not carry it (PR 8). */
	conversationSettings?: string | null;
}

export interface TweetPage {
	tweets: Tweet[];
	/** How many rows X sent that carry no usable id. Shown, never hidden. */
	droppedWithoutIdStr: number;
	/** The timeline asked for, or null when none was — X's own default is NULLCAST. */
	timeline: string | null;
	/**
	 * The ids a read by id asked for, or null for a page. 🔴 An echo of the request, not
	 * a read-back: the rows above are X's answer, and only they carry ids.
	 */
	askedFor: string[] | null;
	page: { pageSize: number; nextCursor: string | null };
	truncated: boolean;
}

/**
 * The answer of `POST /api/v1/media` and `GET /api/v1/media/:id` — X's upload as the
 * contract shapes it. `id` is the numeric upload id (what the status read is asked
 * with), `mediaKey` is what a tweet is made with, and `processing` is X's
 * `processing_info` or null — null is X sending none, which the reference reads as
 * ready; the wizard asks again while the state is pending or in_progress.
 */
export interface MediaView {
	id: string;
	mediaKey: string | null;
	kind: "IMAGE" | "GIF" | null;
	size: number | null;
	processing: {
		state: string | null;
		checkAfterSecs: number | null;
		progressPercent: number | null;
	} | null;
}

/** What `POST /api/v1/tweets` takes. The worker resolves `asUserId` when it is absent. */
export interface TweetCreate {
	account: string;
	text?: string;
	name?: string;
	mediaKeys?: string[];
	asUserId?: string;
	/** A card's `card://…` from `POST /api/v1/cards`; never beside `mediaKeys`, never beside a link in `text`. */
	cardUri?: string;
	/** Who can reply — one of X's three; absent, X's default (EVERYONE) applies. */
	conversationSettings?: ConversationSettings;
}

/** `conversation_settings`' values, verbatim (POST …/tweet): «`EVERYONE`, `FOLLOWING`, `MENTIONED_USERS`». */
export type ConversationSettings = "EVERYONE" | "FOLLOWING" | "MENTIONED_USERS";

/** `POST /api/v1/cards`' body — the worker's closed list, and nothing else. */
export interface CardCreate {
	account: string;
	name: string;
	mediaKey: string;
	title: string;
	url: string;
}

/**
 * One row of `GET /api/v1/promoted-tweets` — a creative attached to a line item.
 *
 * 🔴 `tweetId` is **null when X sent the tweet id as a number**, and the row still
 * arrives. The arithmetic is the same as `Tweet.id`; the right answer is the opposite
 * one. A candidate tweet with no usable id is dropped by the worker, because a wrong id
 * invites promoting a tweet nobody chose — but this row is a promoted tweet that EXISTS
 * in the account and has to be deletable, so it is shown with `tweetId: null`, which
 * says X's answer cannot name the tweet rather than naming the wrong one. `id` is the
 * promoted tweet's own, and that is what Remove needs.
 */
export interface PromotedTweet {
	id: string;
	lineItemId: string;
	tweetId: string | null;
	/** ACTIVE from the moment X creates it: X allows no PUT on this object. */
	entityStatus: string;
	/** X's review of the creative. A promoted tweet can exist and not be shown. */
	approvalStatus: string | null;
	createdAt: string | null;
	deleted: boolean;
}

export interface PromotedTweetPage {
	promotedTweets: PromotedTweet[];
	page: { pageSize: number; nextCursor: string | null };
	truncated: boolean;
}

/** One row of `GET /api/v1/targeting/locations` — the vocabulary a narrowing is built from. */
export interface TargetingLocation {
	/** X's opaque id, and the only field a criterion needs from here. */
	targetingValue: string;
	name: string;
	locationType: string | null;
	countryCode: string | null;
	targetingType: string | null;
}

/** One row of `GET /api/v1/targeting/languages` — the value a LANGUAGE criterion takes. */
/** One row of `GET /api/v1/targeting/interests` (PR 4). */
export interface TargetingInterest {
	/** X's interest value — `"1001"` in the reference's example. */
	targetingValue: string;
	/** X's name, a path — «Books and literature/Biographies and memoirs». */
	name: string;
	targetingType: string | null;
}

export interface TargetingLanguage {
	/** X's language value — `"en"` for English in the reference's example. */
	targetingValue: string;
	name: string;
	targetingType: string | null;
}

/** One row of `GET /api/v1/targeting/platforms` — Ads Manager's Operating system. */
export interface TargetingPlatform {
	/** X's platform value — `"0"` for iOS in the reference's example. */
	targetingValue: string;
	name: string;
	targetingType: string | null;
}

/** One row of `GET /api/v1/targeting/devices` — Ads Manager's Device model. */
export interface TargetingDevice {
	/** X's opaque device value (`"1q"` in the reference's example). */
	targetingValue: string;
	name: string;
	manufacturer: string | null;
	osType: string | null;
	targetingType: string | null;
}

/** One row of `GET /api/v1/targeting/network-operators` — Ads Manager's Carrier. */
export interface TargetingNetworkOperator {
	/** X's opaque carrier value (`"2l"` in the reference's example). */
	targetingValue: string;
	name: string;
	countryCode: string | null;
	targetingType: string | null;
}

/**
 * One row of `GET /api/v1/custom-audiences` — Ads Manager's Custom audiences. `targetable` and
 * `reasonsNotTargetable` are X's, untouched; `targetableTypes` is for reading only.
 */
export interface CustomAudience {
	id: string;
	name: string;
	targetable: boolean;
	reasonsNotTargetable: string[];
	targetableTypes: string[];
	audienceType: string | null;
	audienceSize: number | null;
	permissionLevel: string | null;
}

/** The types `POST /api/v1/targeting-criteria` takes — the worker's `TARGETING_TYPES` (docs/API-CONTRACT.md). */
export type CriterionType =
	| "LOCATION"
	| "LANGUAGE"
	| "GENDER"
	| "AGE"
	| "PLATFORM"
	| "DEVICE"
	| "NETWORK_OPERATOR"
	| "INTEREST"
	| "BROAD_KEYWORD"
	| "PHRASE_KEYWORD"
	| "EXACT_KEYWORD"
	| "SIMILAR_TO_FOLLOWERS_OF_USER"
	| "CUSTOM_AUDIENCE";

/** One row of `GET /api/v1/targeting-criteria` — what narrows one line item. */
export interface TargetingCriterion {
	id: string;
	lineItemId: string;
	targetingType: string;
	targetingValue: string;
	/** X's name for it, `null` when X gave none (since PR 4). */
	name: string | null;
	locationType: string | null;
	operatorType: string | null;
	createdAt: string;
	updatedAt: string;
}

/** What `POST /api/v1/line-items` takes. The flight is the operator's, see the contract. */
/**
 * One entity's figures from `GET /api/v1/stats`, exactly as the worker hands them on.
 *
 * 🔴 **Arrays, and `null` is `null`.** X sends one entry per bucket of the granularity
 * asked for, and `null` — not `[0]` — for a metric it holds nothing for. The worker
 * passes both shapes through untouched, so this type carries them and `lib/stats.ts`
 * decides what a screen may say about each.
 */
export interface StatsRow {
	id: string;
	billedChargeLocalMicro: number[] | null;
	impressions: number[] | null;
	engagements: number[] | null;
	/** X's `url_clicks` (since twads#174): «Total clicks on the link or Website Card in an ad, including earned.» `null` when X sent none. */
	urlClicks: number[] | null;
	/** X's `clicks`: «Total number of clicks, including favorites and other engagements.» Read, not shown as a column. */
	clicks: number[] | null;
}

/**
 * The answer, with the question inside it.
 *
 * `entity`, `granularity`, `placement`, `metricGroups` and `window` are what was asked,
 * echoed so a figure carries its own definition: `placement` in particular, because a
 * figure for one placement is not an account's spend, and `window.end` is **exclusive**.
 * `readAt` is when the worker had X's answer, which is not when the figure became final
 * — X does not say when that is.
 */
export interface StatsAnswer {
	entity: string;
	granularity: string;
	placement: string;
	metricGroups: string[];
	window: { start: string; end: string };
	rows: StatsRow[];
	readAt: string;
}

/** The objectives v1 offers — the contract's closed list; `line-item-copy.ts` carries the words. */
export type LineItemObjective = "ENGAGEMENTS" | "WEBSITE_CLICKS";

export interface LineItemCreate {
	account: string;
	campaignId: string;
	startTime: string;
	endTime: string;
	/** Absent → ENGAGEMENTS on the worker; the screen always sends what its Select shows. */
	objective: LineItemObjective;
	/** Optional; sent only when given (the reference: «The name for the line item»). */
	name?: string;
	/** Optional; micro units, sent only when given. */
	totalBudgetAmountLocalMicro?: number;
	/** Micro units; sent only when the budget is on each ad group — the worker checks the parent. */
	dailyBudgetAmountLocalMicro?: number;
	/** Optional (PR 5); absent → ALL_ON_TWITTER on the worker. */
	placements?: string[];
	/** Optional (PR 5); absent → AUTO on the worker. */
	bidStrategy?: "AUTO" | "MAX" | "TARGET";
	/** Micro units; required under MAX and TARGET, refused under AUTO. */
	bidAmountLocalMicro?: number;
	/** Optional (PR 5); absent → X's default for the objective. */
	goal?: string;
	/** PR 6: the cap with its window (1, 7 or 30 days), or neither; ENGAGEMENTS only. */
	frequencyCap?: number;
	durationInDays?: number;
	/** PR 6: BROAD, DEFINED or EXPANDED — X's three, undescribed by its reference. */
	audienceExpansion?: string;
	/** PR 6: true standard, false accelerated; only under a LINE_ITEM parent. */
	standardDelivery?: boolean;
}

/**
 * What `PUT /api/v1/campaigns/:id` takes: only the fields given are sent, so every one is
 * optional here and the screen sends what changed (lib/campaign-manage.ts).
 */
export interface CampaignUpdate {
	account: string;
	name?: string;
	dailyBudgetAmountLocalMicro?: number;
	totalBudgetAmountLocalMicro?: number;
}

/** What the field form of `PUT /api/v1/line-items/:id` takes — never beside `status`. */
export interface LineItemUpdate {
	account: string;
	name?: string;
	startTime?: string;
	endTime?: string;
	totalBudgetAmountLocalMicro?: number;
	dailyBudgetAmountLocalMicro?: number;
}

/** One row of `GET /api/v1/funding-instruments`. Every `*LocalMicro` is X's, unconverted. */
export interface FundingInstrument {
	id: string;
	type: string;
	entityStatus: string;
	currency: string | null;
	creditLimitLocalMicro: number | null;
	creditRemainingLocalMicro: number | null;
	fundedAmountLocalMicro: number | null;
	ableToFund: boolean;
	reasonsNotAbleToFund: string[];
	startTime: string | null;
	endTime: string | null;
	description: string | null;
}

export type OrgRole = "owner" | "manager" | "member";

export interface Team {
	id: string;
	name: string;
	slug: string;
	status: "pending" | "active" | "suspended" | "rejected";
	requestedBy: string;
	note: string | null;
	memberCount?: number;
	grants?: Grant[];
}

export interface TeamMember {
	id: string;
	userId: string;
	email: string | null;
	role: OrgRole;
	status: string;
}

/** What the console renders for this caller — see lib/principal.ts. */
export interface Standing {
	userId: string;
	email: string | null;
	standing: "platform" | "tenant" | "pending" | "unaffiliated";
	platform: { role: Role; inherited: boolean } | null;
	team: {
		id: string;
		name: string;
		status: Team["status"];
		role: OrgRole;
		grants: Grant[];
	} | null;
}

/** One dependency check. `target` names the exact database/dataset reached. */
export interface Check {
	state: "ok" | "error" | "skipped";
	target: string;
	detail?: string;
}

export interface Smoke {
	db?: Check;
	bq?: Check;
	x?: Check;
}

export interface ColumnInfo {
	name: string;
	type: string;
	nullable: boolean;
	description: string | null;
}

export interface IndexInfo {
	name: string;
	definition: string;
	unique: boolean;
	primary: boolean;
}

export interface ConstraintInfo {
	name: string;
	definition: string;
	kind: "check" | "foreign key";
}

export interface TableInfo {
	name: string;
	description: string | null;
	rows: number | null;
	columns: ColumnInfo[];
	/** Always present, `[]` for BigQuery. Worker script and Static Assets go up
	 *  in one `wrangler deploy`, so this SPA never meets a worker that does not
	 *  send it. */
	indexes: IndexInfo[];
	constraints: ConstraintInfo[];
}

export interface StoreSchema {
	label: string;
	tables: TableInfo[];
	error?: string;
}

export interface Schema {
	cloudsql: StoreSchema;
	bigquery: StoreSchema;
}

/**
 * One line of the append-only trail.
 *
 * Snake_case on purpose, and against this project's own convention: the audit
 * endpoint returns raw Postgres rows rather than mapping them the way every
 * other store function does, so `actor_id` and `created_at` are what actually
 * arrives on the wire. Typing it honestly here beats a camelCase interface that
 * would be quietly wrong at runtime. If the endpoint is ever fixed to match
 * CLAUDE.md, this interface is where it shows.
 */
export interface AuditEntry {
	actor_id: string;
	action: string;
	subject_id: string | null;
	detail: Record<string, unknown> | null;
	created_at: string;
}

// ── Calls ────────────────────────────────────────────────────────────

export const api = {
	me: () => getJson<Me>("/api/me"),
	smoke: () => getJson<Smoke>("/api/v1/smoke"),

	standing: () => getJson<Standing>("/api/v1/orgs/me"),
	registerTeam: (payload: { name: string; note?: string }) =>
		send<{ team: Team }>("POST", "/api/v1/orgs", payload),
	teams: () =>
		getJson<{ teams: Team[]; accountHolders: Record<string, { orgId: string; orgName: string }> }>(
			"/api/v1/orgs",
		),
	setTeamStatus: (orgId: string, status: Team["status"]) =>
		send<{ team: Team }>("PUT", `/api/v1/orgs/${orgId}/status`, { status }),
	setTeamGrants: (
		orgId: string,
		payload: { entity_type: EntityType; permission: Permission; entity_ids: string[] },
	) => send<{ ok: true }>("PUT", `/api/v1/orgs/${orgId}/grants`, payload),
	teamMembers: (orgId: string) =>
		getJson<{ members: TeamMember[] }>(`/api/v1/orgs/${orgId}/members`),
	setTeamMember: (orgId: string, userId: string, payload: { role: OrgRole; email?: string }) =>
		send<{ member: TeamMember }>("PUT", `/api/v1/orgs/${orgId}/members/${userId}`, payload),
	removeTeamMember: (orgId: string, userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/orgs/${orgId}/members/${userId}`),

	myAccess: () => getJson<MyAccess>("/api/v1/access/me"),
	members: () => getJson<{ members: Member[] }>("/api/v1/access/members"),
	setMember: (userId: string, payload: { role: Role; status?: string; email?: string }) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}`,
			payload,
		),
	removeMember: (userId: string) =>
		send<{ ok: true }>("DELETE", `/api/v1/access/members/${encodeURIComponent(userId)}`),
	/** Replaces the whole set for one entity type + permission. */
	setGrants: (
		userId: string,
		payload: { entity_type: EntityType; permission: Permission; entity_ids: string[] },
	) =>
		send<{ member: Member }>(
			"PUT",
			`/api/v1/access/members/${encodeURIComponent(userId)}/grants`,
			payload,
		),

	/*
	 * `truncated` is required on both lists here, and on `/api/v1/x-accounts` below.
	 *
	 * Not optional-because-the-server-might-not-send-it: every one of those
	 * endpoints returns it unconditionally, including the platform-admin branch
	 * that reads no mirror and reports `false` anyway. And the worker script and
	 * the Static Assets go up in a single `wrangler deploy`, so this SPA never
	 * meets a worker that predates the field.
	 *
	 * Written here rather than only in the commit that removed the `?`, because
	 * that is the mistake this line exists to stop: the same `?` was taken out of
	 * `TableInfo.indexes` for this same reason and reappeared here two diffs
	 * later. A guard held up by its explanation comes back whenever the
	 * explanation sounds prudent again — so the refutation has to live where the
	 * guard lived, not in the history.
	 */
	agents: () => getJson<{ agents: Agent[]; truncated: boolean }>("/api/v1/agents"),
	createAgent: (payload: { name: string; kind?: string; description?: string }) =>
		send<{ agent: Agent }>("POST", "/api/v1/agents", payload),
	updateAgent: (id: string, payload: { name?: string; kind?: string; status?: string }) =>
		send<{ agent: Agent }>("PATCH", `/api/v1/agents/${id}`, payload),
	/** The kit's PATCH with `subject` alone: its 409 and 503 mean something, so they come back. */
	setAgentSubject: (id: string, payload: { subject: string | null }) =>
		answer("PATCH", `/api/v1/agents/${encodeURIComponent(id)}`, payload),
	deleteAgent: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/agents/${id}`),
	agentControls: (id: string) =>
		getJson<AgentControlsAnswer>(`/api/v1/agents/${encodeURIComponent(id)}/controls`),
	/** One agent's MCP calls, newest first: the card's panel. */
	agentMcpCalls: (id: string, limit: number) =>
		getJson<McpCallsAnswer>(`/api/v1/mcp-calls?agent=${encodeURIComponent(id)}&limit=${limit}`),
	/** Answered, not thrown: a 400 names the figure or level that was refused. */
	setAgentPolicy: (
		id: string,
		payload: {
			capDayMicro: number | null;
			capTotalMicro: number | null;
			levels: Record<string, ActionLevel>;
		},
	) => answer("PUT", `/api/v1/agents/${encodeURIComponent(id)}/policy`, payload),
	setAgentScope: (
		id: string,
		payload: {
			accounts: {
				xAccountId: string;
				permission: "read" | "write";
				campaignIds: string[] | null;
			}[];
		},
	) => answer("PUT", `/api/v1/agents/${encodeURIComponent(id)}/scope`, payload),

	proxies: () => getJson<{ proxies: ProxyRow[]; truncated: boolean }>("/api/v1/proxies"),
	createProxy: (payload: {
		label: string;
		protocol?: string;
		host: string;
		port: number;
		country?: string;
		cred_ref?: string;
	}) => send<{ proxy: ProxyRow }>("POST", "/api/v1/proxies", payload),
	updateProxy: (
		id: string,
		payload: { label?: string; host?: string; port?: number; country?: string; status?: string },
	) => send<{ proxy: ProxyRow }>("PATCH", `/api/v1/proxies/${id}`, payload),
	deleteProxy: (id: string) => send<{ ok: true }>("DELETE", `/api/v1/proxies/${id}`),

	xAccounts: () => getJson<{ accounts: XAccount[]; truncated: boolean }>("/api/v1/x-accounts"),
	/** One account's approval queue, newest first; `unavailable` names a migration not yet applied. */
	approvals: (account: string) =>
		getJson<{ approvals: Approval[]; truncated: boolean; unavailable?: string }>(
			`/api/v1/approvals?account=${encodeURIComponent(account)}`,
		),
	/**
	 * Answered, not thrown: Approve's answer IS the route's answer — its status and body — and a
	 * 409 says why nothing was sent. 🔴 Never retried here: a 5xx is `unknown` on the row.
	 */
	approveApproval: (id: string) =>
		answer("POST", `/api/v1/approvals/${encodeURIComponent(id)}/approve`),
	rejectApproval: (id: string) =>
		answer("POST", `/api/v1/approvals/${encodeURIComponent(id)}/reject`),
	syncXAccounts: () => send<{ synced: number }>("POST", "/api/v1/x-accounts/sync"),

	/**
	 * `account` is X's ad account id. Read live from X, one page at a time.
	 *
	 * `cursor` is `page.nextCursor` from the page before, sent back untouched: X
	 * calls it opaque and this console never builds one. No `cursor` is the first
	 * page — an empty one is a 400, so an absent cursor is left off the URL, not
	 * sent as `cursor=`.
	 */
	campaigns: (account: string, cursor?: string) =>
		getJson<CampaignPage>(
			`/api/v1/campaigns?account=${encodeURIComponent(account)}${
				cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""
			}`,
		),
	fundingInstruments: (account: string) =>
		getJson<{ fundingInstruments: FundingInstrument[]; truncated: boolean }>(
			`/api/v1/funding-instruments?account=${encodeURIComponent(account)}`,
		),
	/**
	 * X's words about the account's objects, from the mirror (`GET /api/v1/signals`):
	 * the scheduled cycle wrote them, X is not asked on this read. The active rows
	 * only — no `since` from this screen.
	 */
	signals: (account: string) =>
		getJson<SignalsAnswer>(`/api/v1/signals?account=${encodeURIComponent(account)}`),
	/**
	 * The account's figures over its last full days and today so far, from the mirror the
	 * hourly cycle writes (`GET /api/v1/overview-stats`) — no call to X from this screen.
	 */
	overviewStats: (account: string) =>
		getJson<OverviewStatsAnswer>(
			`/api/v1/overview-stats?account=${encodeURIComponent(account)}&days=7`,
		),
	/**
	 * One campaign, by id. **Answered, not thrown**, because a 404 here is a fact and
	 * not a fault: after a create it is the difference between "X does not have it"
	 * and "the read failed", and the lock on Create turns on exactly that.
	 *
	 * Read by id rather than by list on purpose: X's list lags behind a create by
	 * minutes and this read does not (docs/API-CONTRACT.md, the list's lag; twads#86).
	 */
	campaign: (id: string, account: string) =>
		answer(
			"GET",
			`/api/v1/campaigns/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),
	/** Answered, not thrown: see `answer`, and `createOutcome` in lib/campaigns.ts. */
	createCampaign: (payload: CampaignCreate) => answer("POST", "/api/v1/campaigns", payload),
	/** Answered, not thrown: see `statusOutcome` in lib/campaigns.ts. */
	setCampaignStatus: (id: string, payload: CampaignSwitch) =>
		answer("POST", `/api/v1/campaigns/${encodeURIComponent(id)}/status`, payload),
	/**
	 * Change a campaign's name or budgets (twads#171). **Answered, not thrown**: a 502 here
	 * carries `mismatch` — the fields X reports differently from what was sent — which is
	 * a sentence to read, not a failed promise (`updateOutcome` in lib/campaign-manage.ts).
	 */
	updateCampaign: (id: string, payload: CampaignUpdate) =>
		answer("PUT", `/api/v1/campaigns/${encodeURIComponent(id)}`, payload),
	/**
	 * Delete a campaign (twads#171). Answered, not thrown: a 409 says «pause it first», a
	 * 502 can carry `deleted: true` with a failed listing, and `deleted: "unknown"` is
	 * retryable — each a sentence (`deleteOutcome` in lib/campaign-manage.ts).
	 */
	deleteCampaign: (id: string, account: string) =>
		answer(
			"DELETE",
			`/api/v1/campaigns/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),

	/**
	 * One campaign's line items, live from X, one page at a time.
	 *
	 * `campaign` goes to the worker, which sends it to X as `campaign_ids` — never
	 * applied to a page in hand, because a campaign whose rows are on the next page
	 * would otherwise read as a campaign with none (docs/API-CONTRACT.md).
	 */
	lineItems: (account: string, campaign: string, cursor?: string) =>
		getJson<LineItemPage>(
			`/api/v1/line-items?account=${encodeURIComponent(account)}&campaign=${encodeURIComponent(campaign)}${
				cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""
			}`,
		),
	/**
	 * One line item, by id. **Answered, not thrown**, for the reason `campaign` is:
	 * after a create, a 404 is a fact and the lock on Create turns on it.
	 *
	 * This is the read that does not lag behind a create — the list does, and for a
	 * line item there is nothing else to look at.
	 */
	lineItem: (id: string, account: string) =>
		answer(
			"GET",
			`/api/v1/line-items/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),
	/** Answered, not thrown: see `lineItemCreateOutcome` in lib/line-items.ts. */
	createLineItem: (payload: LineItemCreate) => answer("POST", "/api/v1/line-items", payload),

	/**
	 * The account's tweets, which is where a creative's id comes from.
	 *
	 * 🔴 Both switches are sent explicitly and neither is defaulted here: X's own
	 * default timeline is `NULLCAST` — promoted-only tweets — so a screen that sent
	 * nothing would quietly show an ads-only list to someone asking for "our tweets".
	 */
	tweets: (account: string, type: string, timeline: string) =>
		getJson<TweetPage>(
			`/api/v1/tweets?account=${encodeURIComponent(account)}&type=${encodeURIComponent(type)}&timeline=${encodeURIComponent(timeline)}`,
		),
	/**
	 * The same read, by id. 🔴 No timeline: a read by id is a read of those ids, and the
	 * worker refuses the pair. The ids are a query — what comes back is X's row, and its
	 * `id` is X's `id_str`, which is the only id the attach may ever send.
	 */
	tweetsById: (account: string, type: string, ids: string[]) =>
		getJson<TweetPage>(
			`/api/v1/tweets?account=${encodeURIComponent(account)}&type=${encodeURIComponent(type)}&ids=${encodeURIComponent(ids.join(","))}`,
		),

	/**
	 * X's location ids, for building a narrowing. Gated on standing rather than on
	 * holdings, so this answers for an operator with no account lent to them yet.
	 */
	targetingLocations: (q: string, locationType?: string, pageSize?: number) =>
		getJson<{ locations: TargetingLocation[]; truncated: boolean }>(
			// 🔴 An empty `q` is left off, not sent as `q=`: the worker defaults nothing on
			// this route, so `q=` would reach X as a search for nothing. The campaign card
			// reads the whole country list this way (`locationsQuery`, lib/campaign-card.ts).
			`/api/v1/targeting/locations${locationsQuery(q, locationType, pageSize)}`,
		),
	/**
	 * What narrows one line item. 🔴 An empty list is not an error and is the answer
	 * this screen most needs: X reports no criteria, and by X's documentation such a
	 * line item targets everyone.
	 */
	targetingCriteria: (account: string, lineItem: string) =>
		getJson<{ criteria: TargetingCriterion[]; truncated: boolean }>(
			`/api/v1/targeting-criteria?account=${encodeURIComponent(account)}&lineItem=${encodeURIComponent(lineItem)}`,
		),
	/** X's language values, for a LANGUAGE criterion; gated on standing, as the locations lookup is. */
	targetingLanguages: (q: string) =>
		getJson<{ languages: TargetingLanguage[]; truncated: boolean }>(
			// An empty `q` is left off, as on the locations lookup: `q=` would reach X as a search for nothing.
			`/api/v1/targeting/languages${q.trim() === "" ? "" : `?q=${encodeURIComponent(q.trim())}`}`,
		),
	/** X's interests matching `q` (Advanced targeting, PR 4); an empty `q` is left off, as on the other lookups. */
	targetingInterests: (q: string) =>
		getJson<{ interests: TargetingInterest[]; truncated: boolean }>(
			`/api/v1/targeting/interests${q.trim() === "" ? "" : `?q=${encodeURIComponent(q.trim())}`}`,
		),
	/** X's platforms (Operating system); one page, the field table names no cursor. */
	targetingPlatforms: () =>
		getJson<{ platforms: TargetingPlatform[]; truncated: boolean }>("/api/v1/targeting/platforms"),
	/** X's devices matching `q` (Device model); an empty `q` is left off, as on the other lookups. */
	targetingDevices: (q: string) =>
		getJson<{ devices: TargetingDevice[]; truncated: boolean }>(
			`/api/v1/targeting/devices${q.trim() === "" ? "" : `?q=${encodeURIComponent(q.trim())}`}`,
		),
	/**
	 * X's carriers in one country (Carrier). 🔴 The country is always named: without it X
	 * answers its US list, which under a Polish campaign would read as Poland's.
	 */
	targetingNetworkOperators: (countryCode: string) =>
		getJson<{ networkOperators: TargetingNetworkOperator[]; truncated: boolean }>(
			`/api/v1/targeting/network-operators?countryCode=${encodeURIComponent(countryCode)}`,
		),
	/** The account's own custom audiences (one page), each with X's `targetable` and reasons. */
	customAudiences: (account: string) =>
		getJson<{ customAudiences: CustomAudience[]; truncated: boolean }>(
			`/api/v1/custom-audiences?account=${encodeURIComponent(account)}`,
		),
	/**
	 * Answered, not thrown: a narrowing that may not have happened is read from `created`.
	 * `targetingType` is left off for a place — the worker reads its absence as LOCATION.
	 */
	createTargetingCriterion: (payload: {
		account: string;
		lineItemId: string;
		targetingType?: CriterionType;
		/** `NE` for a place to keep out (2026-10-29); absent is `EQ`, as before. */
		operatorType?: "EQ" | "NE";
		targetingValue: string;
	}) => answer("POST", "/api/v1/targeting-criteria", payload),
	/**
	 * Stop or start one line item. Answered rather than thrown, because a 502 here
	 * carries the worker's own sentence about the consequence — which differs by
	 * direction and is the one a person must read, not an HTTP number.
	 *
	 * The status is the caller's argument rather than a constant: until 2026-10-08
	 * this function sent `"PAUSED"` and nothing else, because the console refused to
	 * switch a line item on. The owner lifted that refusal on 2026-10-08, so the
	 * value is now the screen's to choose and the type says which two it may be.
	 */
	setLineItemStatus: (id: string, account: string, status: "ACTIVE" | "PAUSED") =>
		answer("PUT", `/api/v1/line-items/${encodeURIComponent(id)}`, { account, status }),
	/**
	 * Change a line item's name or dates (twads#171) — the same route as the switch, with
	 * fields and never `status`: the worker refuses the two in one body. Answered, not
	 * thrown, for the reason `updateCampaign` is.
	 */
	updateLineItem: (id: string, payload: LineItemUpdate) =>
		answer("PUT", `/api/v1/line-items/${encodeURIComponent(id)}`, payload),
	/** Delete a line item (twads#171). Answered, not thrown, as `deleteCampaign`. */
	deleteLineItem: (id: string, account: string) =>
		answer(
			"DELETE",
			`/api/v1/line-items/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),
	/**
	 * Remove one targeting criterion (twads#171) — the only change X offers for one; a new
	 * place is a new criterion. Answered, not thrown, as the other deletes.
	 */
	removeTargetingCriterion: (id: string, account: string) =>
		answer(
			"DELETE",
			`/api/v1/targeting-criteria/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),

	/**
	 * What is attached to one line item. Deleted rows are not asked for, so after a
	 * removal the row is simply absent — which is what a deleted promoted tweet looks
	 * like, and not the same as never having existed.
	 */
	promotedTweets: (account: string, lineItem: string) =>
		getJson<PromotedTweetPage>(
			`/api/v1/promoted-tweets?account=${encodeURIComponent(account)}&lineItem=${encodeURIComponent(lineItem)}`,
		),
	/**
	 * One promoted tweet with X's `data` as it came (`?raw=1`, A3). `raw` is X's shape, not
	 * the contract's: read it, never map from it.
	 */
	promotedTweetRaw: (id: string, account: string) =>
		getJson<{ promotedTweet: PromotedTweet; raw: Record<string, unknown> }>(
			`/api/v1/promoted-tweets/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}&raw=1`,
		),
	/**
	 * Upload one image or GIF for a creative — multipart, as the worker's route takes it
	 * (`file`, `kind`, `account`, `asUserId?`). **Answered, not thrown**: the body carries
	 * the media key and X's processing state, both of which the wizard reads. No
	 * content-type header is set here: the browser writes the multipart boundary.
	 */
	uploadMedia: (form: FormData) => answerForm("/api/v1/media", form),
	/** Where an upload's processing stands, by the numeric id the upload answered. */
	mediaStatus: (id: string, account: string) =>
		answer("GET", `/api/v1/media/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`),
	/**
	 * Make a nullcast tweet of the owner's own words and media. **Answered, not thrown**:
	 * `created: true` on a 502 is a tweet that exists, and the wizard must not make it
	 * again (`tweetStep` in lib/create-ad.ts).
	 */
	createTweet: (payload: TweetCreate) => answer("POST", "/api/v1/tweets", payload),
	/**
	 * Make a website card — the image added to X's Media Library, the card, its read-back.
	 * **Answered, not thrown**: `created: true` on a 502 is a card that exists (`cardStep`
	 * in lib/create-ad.ts), and `step: "media_library"` a stop before any card was made.
	 */
	createCard: (payload: CardCreate) => answer("POST", "/api/v1/cards", payload),
	/**
	 * Attach one tweet to one line item. **Answered, not thrown**: a 409 says the line
	 * item is delivering and a 502 can carry `created`, and both are sentences an
	 * operator has to read rather than a failed promise.
	 *
	 * One id per call, because X does not promise all-or-nothing over several.
	 */
	promote: (payload: { account: string; lineItemId: string; tweetIds: string[] }) =>
		answer("POST", "/api/v1/promoted-tweets", payload),
	/**
	 * Detach one. Answered rather than thrown: `deleted: "unknown"` is retryable and
	 * `deleted: false` from X means the creative may still be delivering — neither is a
	 * thrown error's business.
	 */
	removePromotedTweet: (id: string, account: string) =>
		answer(
			"DELETE",
			`/api/v1/promoted-tweets/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`,
		),

	/**
	 * What was spent on named entities, and what it bought.
	 *
	 * 🔴 The window is the caller's and goes on the wire as it is: the worker refuses a
	 * window longer than seven days or a time that is not a whole hour in UTC, **by
	 * name**, so a screen that builds one wrongly is told which rule it broke rather than
	 * handed X's own 400 about a request it never saw.
	 */
	stats: (
		account: string,
		entity: "CAMPAIGN" | "LINE_ITEM",
		ids: readonly string[],
		window: { start: string; end: string },
	) =>
		getJson<StatsAnswer>(
			`/api/v1/stats?account=${encodeURIComponent(account)}&entity=${entity}&ids=${ids
				.map(encodeURIComponent)
				.join(",")}&granularity=TOTAL&start=${encodeURIComponent(
				window.start,
			)}&end=${encodeURIComponent(window.end)}`,
		),

	schema: () => getJson<Schema>("/api/v1/system/schema"),
	audit: () => getJson<{ entries: AuditEntry[] }>("/api/v1/access/audit"),
};
